import {Category} from '@wix/ambassador-wix-events-web/types'
import {ISettingsGetter} from '@wix/tpa-settings'
import {LIST_DISPLAY_STRATEGY} from '@wix/wix-events-commons-statics'
import {TabItem} from '@wix/wix-ui-tpa/cssVars'
import {TFunction} from '@wix/yoshi-flow-editor'
import {ALL_CATEGORIES_FILTER, EMPTY_CATEGORY_FILTER} from '../../../../commons/constants/settings'
import {
  getInitialCategoryId,
  getSelectedCategoryIds,
  isCategoryMenuMode,
} from '../../../../commons/selectors/categories'
import settingsParams from '../../settingsParams'
import {State} from '../types/state'
import {getVeloCategoryId} from './velo-overrides'

export const getSelectedCategoryFilterIds = (getSetting: ISettingsGetter): string[] => {
  const selectedCategoryIds = getSelectedCategoryIds(getSetting)

  return selectedCategoryIds.filter(id => id !== ALL_CATEGORIES_FILTER)
}

export const getLoadedCategories = (state: State) => state.categories.categories

export const getMenuCategories = (state: State, getSetting: ISettingsGetter, t: TFunction): Category[] => {
  const selectedCategoryIds = getSelectedCategoryIds(getSetting)
  const loadedCategories = getLoadedCategories(state)

  return selectedCategoryIds
    .map(categoryId => {
      if (categoryId === ALL_CATEGORIES_FILTER) {
        return {
          id: ALL_CATEGORIES_FILTER,
          name: t('categories_all'),
        } as Category
      }

      return loadedCategories.find(category => category.id === categoryId)
    })
    .filter(Boolean)
}

export const getCategoryTabItems = (state: State, getSetting: ISettingsGetter, t: TFunction): TabItem[] => {
  const categories = getMenuCategories(state, getSetting, t)

  return categories.map(category => ({
    title: category.name,
    id: category.id,
  }))
}

export const getSelectedCategoryTabItem = (state: State, getSetting: ISettingsGetter, t: TFunction) => {
  const categoryTabItems = getCategoryTabItems(state, getSetting, t)
  const selectedCategoryId = getVeloCategoryId(state)
  const targetCategoryId = selectedCategoryId === null ? ALL_CATEGORIES_FILTER : selectedCategoryId

  return categoryTabItems.find(category => category.id === targetCategoryId)
}

export const getInitialCategoryFilter = (getSetting: ISettingsGetter): string[] => {
  const initialCategoryId = getInitialCategoryId(getSetting)

  if (initialCategoryId === ALL_CATEGORIES_FILTER) {
    const selectedIds = getSelectedCategoryFilterIds(getSetting)

    if (!selectedIds.length) {
      return [EMPTY_CATEGORY_FILTER]
    }

    return selectedIds
  }

  return [initialCategoryId]
}

export const getCategoryIdFilter = (getSetting: ISettingsGetter) => {
  const categoryMenuMode = isCategoryMenuMode(getSetting)
  const listDisplayStrategy = getSetting(settingsParams.listDisplayStrategy) as LIST_DISPLAY_STRATEGY
  const categoryId = getSetting(settingsParams.categoryId) as string

  if (categoryMenuMode) {
    return getInitialCategoryFilter(getSetting)
  }

  if (listDisplayStrategy === LIST_DISPLAY_STRATEGY.MULTIPLE_CATEGORIES) {
    const selectedIds = getSelectedCategoryFilterIds(getSetting)

    return selectedIds.length ? selectedIds : [EMPTY_CATEGORY_FILTER]
  }

  if (listDisplayStrategy !== LIST_DISPLAY_STRATEGY.CATEGORY || categoryId === EMPTY_CATEGORY_FILTER) {
    return undefined
  }

  return categoryId
}

export const getInitialFetchCategoryId = (getSetting: ISettingsGetter): string[] | undefined => {
  const listDisplayStrategy = getSetting(settingsParams.listDisplayStrategy) as LIST_DISPLAY_STRATEGY

  if (listDisplayStrategy === LIST_DISPLAY_STRATEGY.MULTIPLE_CATEGORIES) {
    return getSelectedCategoryFilterIds(getSetting)
  }

  return undefined
}

export const getActiveCategoryName = (state: State, t: TFunction): string => {
  const selectedCategoryId = getVeloCategoryId(state)

  if (selectedCategoryId === null || selectedCategoryId === ALL_CATEGORIES_FILTER) {
    return t('categories_all')
  }

  const loadedCategories = getLoadedCategories(state)
  const selectedCategory = loadedCategories.find(category => category.id === selectedCategoryId)

  return selectedCategory?.name || ''
}
